/**
 * OneClick Session Debug Page
 *
 * Main page for debugging OneClick sessions. Displays return metadata
 * and a list of timesteps for detailed analysis.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Stack,
  Loader,
  Alert,
  Group,
  Button,
} from "@mantine/core";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";
import { fetchReturnDebugInfo } from "../api";
import { ReturnMetadata } from "./components/ReturnMetadata";
import { TimestepsTable } from "./components/TimestepsTable";
import { ClearActionsMenu } from "./components/ClearActionsMenu";
import type { ReturnDebugData } from "../types";

export const OneClickSessionDebug: React.FC = () => {
  const { returnId } = useParams<{ returnId: string }>();
  const [data, setData] = useState<ReturnDebugData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!returnId) {
      setError("Return ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const debugData = await fetchReturnDebugInfo(returnId);
      setData(debugData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [returnId]);

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack align="center" gap="md" py="xl">
          <Loader size="lg" />
          <Text c="dimmed">Loading return debug information...</Text>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Data"
          color="red"
          variant="filled"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No Data Available"
          color="yellow"
        >
          No debug data found for this return.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={1} mb="xs">
              OneClick Session Debug
            </Title>
            <Text c="dimmed" size="sm">
              Debugging information for return {returnId}
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={loadData}
            >
              Refresh
            </Button>
            {returnId && (
              <ClearActionsMenu returnId={returnId} onClearSuccess={loadData} />
            )}
          </Group>
        </Group>

        {/* Return Metadata */}
        <ReturnMetadata returnInfo={data.returnInfo} />

        {/* Timesteps Table */}
        <TimestepsTable
          timesteps={data.timesteps}
          returnId={data.returnInfo.id}
        />
      </Stack>
    </Container>
  );
};
